import { useEffect, useState } from 'react'
import {
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Button,
  Input,
  Field,
  NativeSelect,
  Switch,
  Checkbox,
  SimpleGrid,
  Spinner,
  Alert,
  IconButton,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FiPlus, FiX } from 'react-icons/fi'
import { toaster } from '@/components/ui/toaster'
import { PageContainer, Card, DesktopOnlyNotice } from '../../components/shared'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useAuth } from '../../hooks/useAuth'
import { useAvailableStates } from '../../hooks/useAvailableStates'
import { useAvailableDistricts } from '../../hooks/useAvailableDistricts'
import { useDistrictCatalog } from '../../hooks/useDistrictCatalog'
import { useCommitteeOptions } from '../../hooks/useAdminUsers'
import { useUnionSearch } from '../../hooks/useUnion'
import { scopeAdminApi } from '../../lib/api'
import { extractApiMessage } from '../../lib/apiError'
import type { ProductTier } from '../../types/config'
import type { GrantedBy, ScopeDistrictEntry, ScopeStateEntry, DistrictTypeToken } from '../../types/scoping'
import { DISTRICT_TYPE_OPTIONS, CATALOG_FIELD_BY_DISTRICT_TYPE } from '../../types/scoping'

// ---------------------------------------------------------------------------
// Instance-level tier + customer name
// ---------------------------------------------------------------------------
function InstanceTierCard() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['config', 'instance-config'],
    queryFn: () => scopeAdminApi.getInstanceConfig().then((r) => r.data),
  })

  const [tier, setTier] = useState<ProductTier>('Campaign')
  const [customerName, setCustomerName] = useState('')

  useEffect(() => {
    if (!data) return
    setTier(data.productTier)
    setCustomerName(data.customerName ?? '')
  }, [data])

  const save = useMutation({
    mutationFn: () =>
      scopeAdminApi
        .setInstanceConfig({ tier, customerName: customerName.trim() || undefined })
        .then((r) => r.data),
    onSuccess: () => {
      toaster.create({ type: 'success', title: 'Instance settings saved' })
      qc.invalidateQueries({ queryKey: ['config'] })
    },
    onError: (err) => {
      toaster.create({ type: 'error', title: extractApiMessage(err, 'Failed to save instance settings.') })
    },
  })

  return (
    <Card>
      <VStack align="stretch" gap={4}>
        <Heading size="md">Instance settings</Heading>
        {isLoading && <Spinner size="sm" color="accent" />}
        <Field.Root>
          <Field.Label>Product tier</Field.Label>
          <NativeSelect.Root>
            <NativeSelect.Field value={tier} onChange={(e) => setTier(e.target.value as ProductTier)}>
              <option value="Campaign">Campaign</option>
              <option value="Union">Union</option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Field.Root>
        <Field.Root>
          <Field.Label>Customer name</Field.Label>
          <Input
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Optional"
          />
        </Field.Root>
        <HStack justify="flex-end">
          <Button colorPalette="brand" onClick={() => save.mutate()} loading={save.isPending}>
            Save product tier
          </Button>
        </HStack>
      </VStack>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// One state's district rows in the grant editor -- extracted so `useDistrictCatalog(state)` has
// a stable top-level hook call per state (the parent renders one of these per selected state
// inside a `.map()`, where a hook call would violate rules-of-hooks).
// ---------------------------------------------------------------------------
function DistrictStateBox({
  state,
  rows,
  onAddRow,
  onUpdateRow,
  onRemoveRow,
}: {
  state: string
  rows: ScopeDistrictEntry[]
  onAddRow: () => void
  onUpdateRow: (idx: number, field: keyof ScopeDistrictEntry, value: string) => void
  onRemoveRow: (idx: number) => void
}) {
  const { data: catalog } = useDistrictCatalog(state)

  return (
    <Box borderWidth="1px" borderColor="border" borderRadius="md" p={3}>
      <HStack justify="space-between" mb={2}>
        <Text fontWeight="medium">{state} districts</Text>
        <Button size="xs" variant="ghost" onClick={onAddRow}>
          <FiPlus /> Add district
        </Button>
      </HStack>
      {rows.length === 0 && (
        <Text fontSize="sm" color="muted">Whole state (no district rows added)</Text>
      )}
      <VStack align="stretch" gap={2}>
        {rows.map((row, idx) => {
          const catalogField = row.districtType
            ? CATALOG_FIELD_BY_DISTRICT_TYPE[row.districtType as DistrictTypeToken]
            : undefined
          const catalogCodes = catalogField ? catalog?.[catalogField] ?? [] : []
          // Fallback to free text when the catalog has nothing for this state/type (no data
          // loaded yet, or the endpoint genuinely returns no codes for that chamber) so
          // provisioning is never blocked on the voter file having district data.
          const hasCatalogOptions = catalogCodes.length > 0
          // Keep the row's current value selectable even if it isn't one of the catalog's
          // known codes (e.g. loaded from a previously-saved grant) so switching into select
          // mode never silently drops what's stored.
          const codeOptions =
            row.districtCode && !catalogCodes.includes(row.districtCode)
              ? [row.districtCode, ...catalogCodes]
              : catalogCodes

          return (
            <HStack key={idx}>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  aria-label={`${state} district type ${idx + 1}`}
                  value={row.districtType}
                  onChange={(e) => onUpdateRow(idx, 'districtType', e.target.value)}
                >
                  <option value="">Select type…</option>
                  {DISTRICT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.token} value={opt.token}>{opt.label}</option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              {hasCatalogOptions ? (
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    aria-label={`${state} district code ${idx + 1}`}
                    value={row.districtCode}
                    onChange={(e) => onUpdateRow(idx, 'districtCode', e.target.value)}
                  >
                    <option value="">Select code…</option>
                    {codeOptions.map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              ) : (
                <Input
                  size="sm"
                  placeholder="District code"
                  aria-label={`${state} district code ${idx + 1}`}
                  value={row.districtCode}
                  onChange={(e) => onUpdateRow(idx, 'districtCode', e.target.value)}
                />
              )}
              <IconButton
                size="sm"
                variant="ghost"
                aria-label="Remove district"
                onClick={() => onRemoveRow(idx)}
              >
                <FiX />
              </IconButton>
            </HStack>
          )
        })}
      </VStack>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Per-committee locality grant + defaults editor
// ---------------------------------------------------------------------------
function CommitteeScopeEditor({ committeeId }: { committeeId: string }) {
  const qc = useQueryClient()
  const { data: scope, isLoading, isFetched } = useQuery({
    queryKey: ['config', 'committee-scope', committeeId],
    queryFn: () => scopeAdminApi.getCommitteeScope(committeeId),
  })
  const { data: catalogStates } = useAvailableStates()
  const { data: committeeOptions } = useCommitteeOptions()
  const { data: unionsResult } = useUnionSearch({ pageSize: 100 })

  const [isUnrestricted, setIsUnrestricted] = useState(false)
  const [grantedBy, setGrantedBy] = useState<GrantedBy>('Self')
  const [sponsorCommitteeId, setSponsorCommitteeId] = useState('')
  const [selectedStates, setSelectedStates] = useState<Set<string>>(new Set())
  const [districtsByState, setDistrictsByState] = useState<Record<string, ScopeDistrictEntry[]>>({})
  const [homeUnionId, setHomeUnionId] = useState('')
  const [defaultMapState, setDefaultMapState] = useState('')
  const [defaultMapDistrictType, setDefaultMapDistrictType] = useState('')
  const [defaultMapDistrictCode, setDefaultMapDistrictCode] = useState('')
  const [defaultsError, setDefaultsError] = useState<string | null>(null)

  // Populate the form from the loaded scope (or reset to an empty form on the 204/null path).
  useEffect(() => {
    if (!isFetched) return
    if (!scope) {
      setIsUnrestricted(false)
      setGrantedBy('Self')
      setSponsorCommitteeId('')
      setSelectedStates(new Set())
      setDistrictsByState({})
      setHomeUnionId('')
      setDefaultMapState('')
      setDefaultMapDistrictType('')
      setDefaultMapDistrictCode('')
      return
    }
    setIsUnrestricted(scope.isUnrestricted)
    setGrantedBy(scope.grantedBy)
    setSponsorCommitteeId(scope.sponsorCommitteeId ?? '')
    setSelectedStates(new Set(scope.states.map((s) => s.state)))
    const byState: Record<string, ScopeDistrictEntry[]> = {}
    scope.states.forEach((s) => {
      byState[s.state] = s.districts
    })
    setDistrictsByState(byState)
    setHomeUnionId(scope.homeUnionId ?? '')
    setDefaultMapState(scope.defaultMapState ?? '')
    setDefaultMapDistrictType(scope.defaultMapDistrictType ?? '')
    setDefaultMapDistrictCode(scope.defaultMapDistrictCode ?? '')
  }, [scope, isFetched])

  // District options for the (single) default-map state, scoped to this committee's OWN grant
  // (`useAvailableDistricts` -> `/config/available-districts`, which reflects the committee's
  // resolved locality, not the raw voter-file catalog). Not reused for the per-state grant editor
  // below -- that editor is defining the grant itself (a committee doesn't have one yet while
  // it's being configured), so it fetches the voter-file-wide catalog instead: each
  // `DistrictStateBox` calls `useDistrictCatalog(state)`, one fetch per selected state (Task 8).
  const { data: districtsResp } = useAvailableDistricts(defaultMapState || undefined, committeeId)

  const toggleState = (code: string, checked: boolean) => {
    setSelectedStates((prev) => {
      const next = new Set(prev)
      if (checked) next.add(code)
      else next.delete(code)
      return next
    })
    if (checked) {
      setDistrictsByState((prev) => (prev[code] ? prev : { ...prev, [code]: [] }))
    }
  }

  const addDistrictRow = (state: string) => {
    setDistrictsByState((prev) => ({
      ...prev,
      [state]: [...(prev[state] ?? []), { districtType: '', districtCode: '' }],
    }))
  }

  const updateDistrictRow = (
    state: string,
    idx: number,
    field: keyof ScopeDistrictEntry,
    value: string,
  ) => {
    setDistrictsByState((prev) => {
      const rows = [...(prev[state] ?? [])]
      rows[idx] = { ...rows[idx], [field]: value }
      return { ...prev, [state]: rows }
    })
  }

  const removeDistrictRow = (state: string, idx: number) => {
    setDistrictsByState((prev) => ({
      ...prev,
      [state]: (prev[state] ?? []).filter((_, i) => i !== idx),
    }))
  }

  const buildStatesPayload = (): ScopeStateEntry[] =>
    (catalogStates ?? [])
      .filter((s) => selectedStates.has(s.code))
      .map((s) => ({
        state: s.code,
        districts: (districtsByState[s.code] ?? []).filter(
          (d) => d.districtType.trim() && d.districtCode.trim(),
        ),
      }))

  const saveGrant = useMutation({
    mutationFn: () => {
      const payload = {
        isUnrestricted,
        grantedBy,
        ...(grantedBy === 'Sponsored' && sponsorCommitteeId ? { sponsorCommitteeId } : {}),
        states: isUnrestricted ? [] : buildStatesPayload(),
      }
      return scopeAdminApi.setCommitteeGrant(committeeId, payload).then((r) => r.data)
    },
    onSuccess: (data) => {
      toaster.create({ type: 'success', title: 'Grant saved' })
      qc.setQueryData(['config', 'committee-scope', committeeId], data)
      qc.invalidateQueries({ queryKey: ['config', 'available-states'] })
      qc.invalidateQueries({ queryKey: ['config', 'available-districts'] })
    },
    onError: (err) => {
      toaster.create({ type: 'error', title: extractApiMessage(err, 'Failed to save grant.') })
    },
  })

  const saveDefaults = useMutation({
    mutationFn: () => {
      const payload = {
        homeUnionId: homeUnionId || undefined,
        defaultMapState: defaultMapState || undefined,
        defaultMapDistrictType: defaultMapDistrictType || undefined,
        defaultMapDistrictCode: defaultMapDistrictCode || undefined,
      }
      return scopeAdminApi.setCommitteeDefaults(committeeId, payload).then((r) => r.data)
    },
    onSuccess: (data) => {
      setDefaultsError(null)
      toaster.create({ type: 'success', title: 'Defaults saved' })
      qc.setQueryData(['config', 'committee-scope', committeeId], data)
      qc.invalidateQueries({ queryKey: ['config'] })
      qc.invalidateQueries({ queryKey: ['config', 'available-districts'] })
    },
    onError: (err) => {
      setDefaultsError(extractApiMessage(err, 'Failed to save defaults.'))
    },
  })

  const sponsorOptions = (committeeOptions ?? []).filter((c) => c.id !== committeeId)
  const unionOptions = unionsResult?.items ?? []
  const districtOptions = districtsResp?.districts ?? []

  return (
    <VStack align="stretch" gap={6}>
      <Card>
        <VStack align="stretch" gap={4}>
          <Heading size="md">Locality grant</Heading>
          {isLoading && <Spinner size="sm" color="accent" />}
          {isFetched && !scope && (
            <Alert.Root status="info" borderRadius="md">
              <Alert.Indicator />
              No scope configured yet for this committee — showing an empty form.
            </Alert.Root>
          )}

          <Switch.Root
            checked={isUnrestricted}
            onCheckedChange={(e) => setIsUnrestricted(e.checked === true)}
            colorPalette="brand"
          >
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
            <Switch.Label>Unrestricted (all states and districts)</Switch.Label>
          </Switch.Root>

          <Field.Root>
            <Field.Label>Granted by</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={grantedBy}
                onChange={(e) => setGrantedBy(e.target.value as GrantedBy)}
              >
                <option value="Self">Self</option>
                <option value="Sponsored">Sponsored</option>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          {grantedBy === 'Sponsored' && (
            <Field.Root>
              <Field.Label>Sponsor committee</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field
                  value={sponsorCommitteeId}
                  onChange={(e) => setSponsorCommitteeId(e.target.value)}
                >
                  <option value="">Select a sponsor…</option>
                  {sponsorOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
          )}

          {!isUnrestricted && (
            <VStack align="stretch" gap={3}>
              <Text fontWeight="medium">States</Text>
              <SimpleGrid columns={{ base: 2, md: 4 }} gap={2}>
                {(catalogStates ?? []).map((s) => (
                  <Checkbox.Root
                    key={s.code}
                    checked={selectedStates.has(s.code)}
                    onCheckedChange={(e) => toggleState(s.code, e.checked === true)}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control>
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                    <Checkbox.Label>{s.name}</Checkbox.Label>
                  </Checkbox.Root>
                ))}
              </SimpleGrid>

              {Array.from(selectedStates).map((state) => (
                <DistrictStateBox
                  key={state}
                  state={state}
                  rows={districtsByState[state] ?? []}
                  onAddRow={() => addDistrictRow(state)}
                  onUpdateRow={(idx, field, value) => updateDistrictRow(state, idx, field, value)}
                  onRemoveRow={(idx) => removeDistrictRow(state, idx)}
                />
              ))}
            </VStack>
          )}

          <HStack justify="flex-end">
            <Button colorPalette="brand" onClick={() => saveGrant.mutate()} loading={saveGrant.isPending}>
              Save grant
            </Button>
          </HStack>
        </VStack>
      </Card>

      <Card>
        <VStack align="stretch" gap={4}>
          <Heading size="md">Committee defaults</Heading>
          {defaultsError && (
            <Alert.Root status="error" borderRadius="md">
              <Alert.Indicator />
              {defaultsError}
            </Alert.Root>
          )}

          <Field.Root>
            <Field.Label>Home union</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field value={homeUnionId} onChange={(e) => setHomeUnionId(e.target.value)}>
                <option value="">None</option>
                {unionOptions.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label>Default map state</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={defaultMapState}
                onChange={(e) => {
                  setDefaultMapState(e.target.value)
                  setDefaultMapDistrictType('')
                  setDefaultMapDistrictCode('')
                }}
              >
                <option value="">None</option>
                {(catalogStates ?? []).map((s) => (
                  <option key={s.code} value={s.code}>{s.name}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          {/* `disabled` must live on Field.Root, not NativeSelect.Root -- a Field.Root
              ancestor silently overrides NativeSelect.Root's own `disabled` prop in Chakra v3
              (see memory/chakra-v3-field-root-disabled-override.md). */}
          <Field.Root disabled={!defaultMapState}>
            <Field.Label>Default map district</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={
                  defaultMapDistrictType && defaultMapDistrictCode
                    ? `${defaultMapDistrictType}|${defaultMapDistrictCode}`
                    : ''
                }
                onChange={(e) => {
                  const [type, code] = e.target.value.split('|')
                  setDefaultMapDistrictType(type ?? '')
                  setDefaultMapDistrictCode(code ?? '')
                }}
              >
                <option value="">Whole state</option>
                {districtOptions.map((d) => (
                  <option key={`${d.districtType}|${d.districtCode}`} value={`${d.districtType}|${d.districtCode}`}>
                    {d.districtType} — {d.districtCode}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          <HStack justify="flex-end">
            <Button colorPalette="brand" onClick={() => saveDefaults.mutate()} loading={saveDefaults.isPending}>
              Save defaults
            </Button>
          </HStack>
        </VStack>
      </Card>
    </VStack>
  )
}

// ---------------------------------------------------------------------------
// Page shell: SuperAdmin gate, mobile fork, committee picker
// ---------------------------------------------------------------------------
export function ScopeConfigPage() {
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const { data: committeeOptions } = useCommitteeOptions()
  const [committeeId, setCommitteeId] = useState('')

  // AdminGuard (the route wrapper) only enforces the generic Admin tier -- SuperAdmin ⊇ Admin,
  // so a plain Admin can reach this route. The real gate is the backend's [Authorize] on all
  // five scope-admin endpoints (403 for non-SuperAdmin); this is a UX-only fail-closed notice so
  // an Admin never sees a broken editor.
  const isSuperAdmin = user?.role === 'SuperAdmin'

  if (!isSuperAdmin) {
    return (
      <PageContainer title="Scope configuration">
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          SuperAdmin privileges are required to view this page.
        </Alert.Root>
      </PageContainer>
    )
  }

  if (isMobile) {
    return (
      <DesktopOnlyNotice
        title="Scope configuration is a desktop tool"
        message="Configuring the product tier and committee locality access needs a wider screen."
        desktopHint="Switch to a desktop or tablet to continue."
      />
    )
  }

  return (
    <PageContainer
      title="Scope configuration"
      subtitle="Product tier, and each committee's state / district locality access"
    >
      <VStack align="stretch" gap={6}>
        <InstanceTierCard />

        <Card>
          <VStack align="stretch" gap={4}>
            <Heading size="md">Committee</Heading>
            <Field.Root>
              <Field.Label>Committee</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field value={committeeId} onChange={(e) => setCommitteeId(e.target.value)}>
                  <option value="">Select a committee…</option>
                  {(committeeOptions ?? []).map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
          </VStack>
        </Card>

        {/* Remounts on committee switch so no stale form state from the previous
            committee can flash before the new load effect runs. */}
        {committeeId && <CommitteeScopeEditor key={committeeId} committeeId={committeeId} />}
      </VStack>
    </PageContainer>
  );
}
